import React, { useState } from "react";
import { adminupdateVerification, useAppDispatch, getAllVerifications } from "../../redux";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/Button";
import { motion, AnimatePresence } from "framer-motion";
import {
    Mail,
    X,
    CheckCircle,
    Clock,
    FileText,
    Trash2,
} from "lucide-react";
import Spinner from "../../modules/shared/components/Spinner";
import { VerificationStatusEnum } from "../../modules/shared/model";
import { createUserNotificationAction } from "../../redux/Actions/UserNotificationActions";
import { 
    NotificationPriorityEnum, 
    NotificationStatusEnum, 
    NotificationTypeEnum 
} from "../../modules/shared/model/user-notification.enum";
const VerificationDetailModal = ({ isOpen, onClose, request, clickedItem, onRefresh }) => {
    const [note, setNote] = useState("");
    const [localRequest, setLocalRequest] = useState(request);
    const dispatch = useAppDispatch();
    const isLoading = useSelector(selectLoaderValue);

    // Update local request when prop changes
    React.useEffect(() => {
        setLocalRequest(request);
    }, [request]);

    if (!isOpen || !localRequest || !clickedItem) return null;

    const user = localRequest.userId || {};
    const documents = localRequest[clickedItem] || [];

    const getDocumentLabel = key => {
        switch (key) {
            case "identityFiles":
                return "Identification";
            case "businessFiles":
                return "Business";
            case "addressProofFiles":
                return "Address Proof";
            default:
                return "Document";
        }
    };

    const getStatus = () => {
        const statusKey = clickedItem.replace("Files", "Status");
        return localRequest[statusKey]?.id;
    };

    const getDocumentStatusBadge = statusId => {
        let text = "Pending";
        let color = "bg-yellow-100 text-yellow-700";
        if (statusId === "2") {
            text = "Approved";
            color = "bg-green-100 text-green-700";
        } else if (statusId === "3") {
            text = "Rejected";
            color = "bg-red-100 text-red-700";
        }
        return (
            <Badge variant={undefined} className={color}>
                {text}
            </Badge>
        );
    };

    const handleAddNote = async () => {
        if (!note.trim()) return;

        const notesField = `${clickedItem.replace("Files", "Notes")}`;
        const newNote = note.trim();
        
        // Update local state immediately to show the note
        setLocalRequest(prev => ({
            ...prev,
            [notesField]: [...(prev[notesField] || []), newNote]
        }));

        const payload = {
            userId: { id: user.id },
            identityFiles: localRequest.identityFiles.map(f => ({ id: f.id })),
            businessFiles: localRequest.businessFiles.map(f => ({ id: f.id })),
            addressProofFiles: localRequest.addressProofFiles.map(f => ({ id: f.id })),
            identityStatus: { id: Number(localRequest.identityStatus?.id) || null },
            businessStatus: { id: Number(localRequest.businessStatus?.id) || null },
            addressProofStatus: { id: Number(localRequest.addressProofStatus?.id) || null },
            identityRemarks: localRequest.identityRemarks || "",
            businessRemarks: localRequest.businessRemarks || "",
            addressProofRemarks: localRequest.addressProofRemarks || "",
            identityNotes: localRequest.identityNotes || [],
            businessNotes: localRequest.businessNotes || [],
            addressProofNotes: localRequest.addressProofNotes || [],
            identityAdditionalDocuments: localRequest.identityAdditionalDocuments || [],
            businessAdditionalDocuments: localRequest.businessAdditionalDocuments || [],
            addressProofAdditionalDocuments: localRequest.addressProofAdditionalDocuments || [],
        };
        
        // Add note to the appropriate notes array
        const existingNotes = payload[notesField] || [];
        payload[notesField] = [...existingNotes, newNote];

        await dispatch(adminupdateVerification({ id: localRequest.id, data: payload }));
        
        // Send notification to user about the note
        try {
            await createUserNotificationAction({
                title: "Verification Note Added",
                message: `Admin has added a note to your ${getDocumentLabel(clickedItem).toLowerCase()} verification: "${note.trim()}". Please check your verification status for updates.`,
                status: { id: NotificationStatusEnum.Unread },
                priority: { id: NotificationPriorityEnum.Medium },
                type: { id: NotificationTypeEnum["In-App"] },
                user: { id: user.id },
                url: "/verify"
            });
        } catch (error) {
            console.error("Failed to send notification:", error);
        }
        
        setNote("");
        
        // Refresh the verification data to show the new note
        if (onRefresh) {
            onRefresh();
        }
    };

    const handleRemoveNote = async (noteIndex) => {
        const notesField = `${clickedItem.replace("Files", "Notes")}`;
        const currentNotes = localRequest[notesField] || [];
        
        // Remove the note at the specified index
        const updatedNotes = currentNotes.filter((_, index) => index !== noteIndex);
        
        // Update local state immediately
        setLocalRequest(prev => ({
            ...prev,
            [notesField]: updatedNotes
        }));

        const payload = {
            userId: { id: user.id },
            identityFiles: localRequest.identityFiles.map(f => ({ id: f.id })),
            businessFiles: localRequest.businessFiles.map(f => ({ id: f.id })),
            addressProofFiles: localRequest.addressProofFiles.map(f => ({ id: f.id })),
            identityStatus: { id: Number(localRequest.identityStatus?.id) || null },
            businessStatus: { id: Number(localRequest.businessStatus?.id) || null },
            addressProofStatus: { id: Number(localRequest.addressProofStatus?.id) || null },
            identityRemarks: localRequest.identityRemarks || "",
            businessRemarks: localRequest.businessRemarks || "",
            addressProofRemarks: localRequest.addressProofRemarks || "",
            identityNotes: localRequest.identityNotes || [],
            businessNotes: localRequest.businessNotes || [],
            addressProofNotes: localRequest.addressProofNotes || [],
            identityAdditionalDocuments: localRequest.identityAdditionalDocuments || [],
            businessAdditionalDocuments: localRequest.businessAdditionalDocuments || [],
            addressProofAdditionalDocuments: localRequest.addressProofAdditionalDocuments || [],
        };
        
        // Update the specific notes field with the filtered array
        payload[notesField] = updatedNotes;

        await dispatch(adminupdateVerification({ id: localRequest.id, data: payload }));
        
        // Refresh the verification data
        if (onRefresh) {
            onRefresh();
        }
    };

    const handleAdditionalDocumentDecision = async (docIndex: number, newStatus: "approved" | "rejected") => {
        const additionalDocsField = `${clickedItem.replace("Files", "AdditionalDocuments")}`;
        const currentAdditionalDocs = localRequest[additionalDocsField] || [];
        
        // Update the status of the specific document
        const updatedAdditionalDocs = currentAdditionalDocs.map((doc, index) => 
            index === docIndex ? { ...doc, status: newStatus } : doc
        );
        
        // Update local state immediately
        setLocalRequest(prev => ({
            ...prev,
            [additionalDocsField]: updatedAdditionalDocs
        }));

        const payload = {
            userId: { id: user.id },
            identityFiles: localRequest.identityFiles.map(f => ({ id: f.id })),
            businessFiles: localRequest.businessFiles.map(f => ({ id: f.id })),
            addressProofFiles: localRequest.addressProofFiles.map(f => ({ id: f.id })),
            identityStatus: { id: Number(localRequest.identityStatus?.id) || null },
            businessStatus: { id: Number(localRequest.businessStatus?.id) || null },
            addressProofStatus: { id: Number(localRequest.addressProofStatus?.id) || null },
            identityRemarks: localRequest.identityRemarks || "",
            businessRemarks: localRequest.businessRemarks || "",
            addressProofRemarks: localRequest.addressProofRemarks || "",
            identityNotes: localRequest.identityNotes || [],
            businessNotes: localRequest.businessNotes || [],
            addressProofNotes: localRequest.addressProofNotes || [],
            identityAdditionalDocuments: localRequest.identityAdditionalDocuments || [],
            businessAdditionalDocuments: localRequest.businessAdditionalDocuments || [],
            addressProofAdditionalDocuments: localRequest.addressProofAdditionalDocuments || [],
        };
        
        // Update the specific additional documents field
        payload[additionalDocsField] = updatedAdditionalDocs;

        await dispatch(adminupdateVerification({ id: localRequest.id, data: payload }));
        
        // Send notification to user about the additional document decision
        try {
            const isApproved = newStatus === "approved";
            
            let notificationTitle = "";
            let notificationMessage = "";
            
            if (isApproved) {
                notificationTitle = "Additional Document Approved";
                notificationMessage = `Your additional ${getDocumentLabel(clickedItem).toLowerCase()} document has been approved. Thank you for your cooperation.`;
            } else {
                notificationTitle = "Additional Document Rejected";
                notificationMessage = `Your additional ${getDocumentLabel(clickedItem).toLowerCase()} document has been rejected. Please review and submit a new document if required.`;
            }
            
            await createUserNotificationAction({
                title: notificationTitle,
                message: notificationMessage,
                status: { id: NotificationStatusEnum.Unread },
                priority: { id: isApproved ? NotificationPriorityEnum.Medium : NotificationPriorityEnum.High },
                type: { id: NotificationTypeEnum["In-App"] },
                user: { id: user.id },
                url: "/verify"
            });
        } catch (error) {
            console.error("Failed to send notification:", error);
        }
        
        // Refresh the verification data
        if (onRefresh) {
            onRefresh();
        }
    };

    const handleDecision = async statusId => {
        // Get the field names for the specific document type being updated
        const documentType = clickedItem.replace("Files", ""); // e.g., "identity", "business", "addressProof"
        const filesField = `${documentType}Files`;
        const statusField = `${documentType}Status`;
        const remarksField = `${documentType}Remarks`;
        const notesField = `${documentType}Notes`;
        const additionalDocsField = `${documentType}AdditionalDocuments`;

        // Get the files array safely
        const filesArray = localRequest[filesField];
        const fileIds = Array.isArray(filesArray) && filesArray.length > 0 
            ? filesArray.map(f => ({ id: f.id }))
            : [];

        // Only send data for the specific document type being updated
        const payload = {
            userId: { id: user.id },
            // Only include the files for this specific type
            [filesField]: fileIds,
            // Only include the status for this specific type
            [statusField]: { id: Number(statusId) },
            // Only include the remarks for this specific type
            [remarksField]: note,
            // Only include additional documents for this specific type
            [additionalDocsField]: localRequest[additionalDocsField] || [],
        };
        
        // Add note to the appropriate notes array if there's a note
        if (note.trim()) {
            const existingNotes = localRequest[notesField] || [];
            payload[notesField] = [...existingNotes, note.trim()];
        } else {
            // Include existing notes even if no new note
            payload[notesField] = localRequest[notesField] || [];
        }

        await dispatch(adminupdateVerification({ id: localRequest.id, data: payload }));
        
        // Send notification to user about the decision
        try {
            const isApproved = statusId === VerificationStatusEnum.accepted;
            const isRejected = statusId === VerificationStatusEnum.rejected;
            
            let notificationTitle = "";
            let notificationMessage = "";
            
            if (isApproved) {
                notificationTitle = "Verification Approved";
                notificationMessage = `Great news! Your ${getDocumentLabel(clickedItem).toLowerCase()} verification has been approved. Your account verification is progressing.`;
            } else if (isRejected) {
                notificationTitle = "Verification Requires Attention";
                notificationMessage = `Your ${getDocumentLabel(clickedItem).toLowerCase()} verification requires attention. ${note.trim() ? `Note: "${note.trim()}"` : "Please review and resubmit your documents."}`;
            }
            
            if (notificationTitle && notificationMessage) {
                await createUserNotificationAction({
                    title: notificationTitle,
                    message: notificationMessage,
                    status: { id: NotificationStatusEnum.Unread },
                    priority: { id: isApproved ? NotificationPriorityEnum.Medium : NotificationPriorityEnum.High },
                    type: { id: NotificationTypeEnum["In-App"] },
                    user: { id: user.id },
                    url: "/verify"
                });
            }
        } catch (error) {
            console.error("Failed to send notification:", error);
        }
        
        setNote("");
        
        // Refresh the verification data to show the updated status and notes
        if (onRefresh) {
            onRefresh();
        }
        
        onClose();
    };

    const getStatusColor = status => {
        switch (status) {
            case "completed":
                return "text-green-600";
            case "in_progress":
                return "text-blue-600";
            case "pending":
                return "text-yellow-600";
            default:
                return "text-gray-600";
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-6 border-b sticky top-0 bg-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold">Verification Request Details</h2>
                            <p className="text-sm text-gray-500">ID: {localRequest.id}</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                            <Spinner color="red" />
                        </div>
                    )}

                    {/* Professional Information */}
                    <div className="space-y-4">
                        <h3 className="font-medium">Professional Information</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-sm text-gray-500">Name</span>
                                    <p className="font-medium">
                                        {user.firstName} {user.lastName}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Email</span>
                                    <p className="font-medium">{user.email}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Phone</span>
                                    <p className="font-medium">{user.phone}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Profession</span>
                                    <p className="font-medium">{user.bio || "N/A"}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Hourly Rate</span>
                                    <p className="font-medium">${user.hourlyRate}</p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Type</span>
                                    <Badge variant={undefined} className="bg-blue-100 text-blue-700">
                                        Local
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Documents */}
                    <div className="space-y-4">
                        <h3 className="font-medium">Submitted Documents ({getDocumentLabel(clickedItem)})</h3>
                        <div className="space-y-3">
                            {documents.map((doc, index) => (
                                <div
                                    key={doc.id || index}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <FileText className="h-5 w-5 text-gray-400" />
                                        <div>
                                            <p className="font-medium">Document {index + 1}</p>
                                            <p className="text-sm text-gray-500 capitalize">
                                                {getDocumentLabel(clickedItem)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {getDocumentStatusBadge(getStatus())}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => window.open(doc.path, "_blank")}>
                                            View
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Additional Documents */}
                    {(() => {
                        const additionalDocsField = `${clickedItem.replace("Files", "AdditionalDocuments")}`;
                        const additionalDocs = localRequest[additionalDocsField] || [];
                        
                        if (additionalDocs.length === 0) return null;
                        
                        return (
                            <div className="space-y-4">
                                <h3 className="font-medium">Additional Documents ({getDocumentLabel(clickedItem)})</h3>
                                <div className="space-y-3">
                                    {additionalDocs.map((doc, index) => (
                                        <div
                                            key={doc.id || index}
                                            className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <FileText className="h-5 w-5 text-blue-400" />
                                                    <div>
                                                        <p className="font-medium">Additional Document {index + 1}</p>
                                                        <p className="text-sm text-gray-500 capitalize">
                                                            {getDocumentLabel(clickedItem)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Badge variant={undefined} className={
                                                        doc.status === "approved" ? "bg-green-100 text-green-700" :
                                                        doc.status === "rejected" ? "bg-red-100 text-red-700" :
                                                        "bg-yellow-100 text-yellow-700"
                                                    }>
                                                        {doc.status === "approved" ? "Approved" :
                                                         doc.status === "rejected" ? "Rejected" :
                                                         "Pending"}
                                                    </Badge>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => window.open(doc.path, "_blank")}>
                                                        View
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Action buttons for pending documents */}
                                            {doc.status === "pending" && (
                                                <div className="flex gap-2 pt-3 border-t border-blue-200">
                                                    <Button
                                                        size="sm"
                                                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                                                        onClick={() => handleAdditionalDocumentDecision(index, "approved")}
                                                        disabled={isLoading}
                                                    >
                                                        <CheckCircle className="h-4 w-4" />
                                                        Approve Document
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                                                        onClick={() => handleAdditionalDocumentDecision(index, "rejected")}
                                                        disabled={isLoading}
                                                    >
                                                        <X className="h-4 w-4" />
                                                        Reject Document
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Verification Progress */}
                    {/* <div className="space-y-4">
                            <h3 className="font-medium">Verification Progress</h3>
                            <div className="relative flex flex-col gap-4">
                                <div className="flex items-start gap-3">
                                    <div className="mt-1.5 h-4 w-4 rounded-full border-2 bg-green-600 border-green-600"></div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-green-600">Document Submission</p>
                                            <p className="text-sm text-gray-500">2024-01-15</p>
                                        </div>
                                        <div className="absolute left-2 ml-[-1px] w-0.5 h-8 bg-gray-200"></div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-1.5 h-4 w-4 rounded-full border-2 bg-green-600 border-green-600"></div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-green-600">Initial Review</p>
                                            <p className="text-sm text-gray-500">2024-01-16</p>
                                        </div>
                                        <div className="absolute left-2 ml-[-1px] w-0.5 h-8 bg-gray-200"></div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-1.5 h-4 w-4 rounded-full border-2 bg-gray-200 border-gray-200"></div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-yellow-600">Background Check</p>
                                        </div>
                                        <div className="absolute left-2 ml-[-1px] w-0.5 h-8 bg-gray-200"></div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-1.5 h-4 w-4 rounded-full border-2 bg-gray-200 border-gray-200"></div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-yellow-600">Final Approval</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div> */}

                    {/* Notes */}
                    <div className="space-y-4">
                        <h3 className="font-medium">Notes ({getDocumentLabel(clickedItem)})</h3>
                        <div className="space-y-3">
                            {(() => {
                                const notesField = `${clickedItem.replace("Files", "Notes")}`;
                                const notes = localRequest[notesField] || [];
                                
                                if (notes.length === 0) {
                                    return (
                                        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                                            No notes available for this verification type
                                        </div>
                                    );
                                }
                                
                                return notes.map((noteItem, index) => (
                                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">Admin</span>
                                                <span className="text-sm text-gray-500">
                                                    {new Date().toLocaleDateString()}
                                                </span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                                                onClick={() => {
                                                    if (window.confirm("Are you sure you want to delete this note?")) {
                                                        handleRemoveNote(index);
                                                    }
                                                }}
                                                disabled={isLoading}
                                                title="Delete note"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <p className="mt-2 text-gray-700">{noteItem}</p>
                                    </div>
                                ));
                            })()}
                        </div>
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder={`Add a note for ${getDocumentLabel(clickedItem).toLowerCase()}...`}
                                className="flex-1 px-4 py-2 border rounded-lg"
                            />
                            <Button 
                                className="flex-shrink-0"
                                onClick={handleAddNote}
                                disabled={!note.trim() || isLoading}
                            >
                                {isLoading ? "Adding..." : "Add Note"}
                            </Button>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-4">
                        <h3 className="font-medium">Actions</h3>
                        <div className="flex flex-wrap gap-3">
                            <Button 
                                variant="outline" 
                                className="flex items-center gap-2"
                                onClick={async () => {
                                    const notesField = `${clickedItem.replace("Files", "Notes")}`;
                                    const baseNote = "Require Additional Documents";
                                    // Append custom text if there's any in the note input field
                                    const customText = note.trim();
                                    const newNote = customText ? `${baseNote}: ${customText}` : baseNote;
                                    const verificationType = getDocumentLabel(clickedItem);
                                    
                                    // Update local state immediately to show the note
                                    setLocalRequest(prev => ({
                                        ...prev,
                                        [notesField]: [...(prev[notesField] || []), newNote]
                                    }));

                                    const payload = {
                                        userId: { id: user.id },
                                        identityFiles: localRequest.identityFiles.map(f => ({ id: f.id })),
                                        businessFiles: localRequest.businessFiles.map(f => ({ id: f.id })),
                                        addressProofFiles: localRequest.addressProofFiles.map(f => ({ id: f.id })),
                                        identityStatus: { id: Number(localRequest.identityStatus?.id) || null },
                                        businessStatus: { id: Number(localRequest.businessStatus?.id) || null },
                                        addressProofStatus: { id: Number(localRequest.addressProofStatus?.id) || null },
                                        identityRemarks: localRequest.identityRemarks || "",
                                        businessRemarks: localRequest.businessRemarks || "",
                                        addressProofRemarks: localRequest.addressProofRemarks || "",
                                        identityNotes: localRequest.identityNotes || [],
                                        businessNotes: localRequest.businessNotes || [],
                                        addressProofNotes: localRequest.addressProofNotes || [],
                                        identityAdditionalDocuments: localRequest.identityAdditionalDocuments || [],
                                        businessAdditionalDocuments: localRequest.businessAdditionalDocuments || [],
                                        addressProofAdditionalDocuments: localRequest.addressProofAdditionalDocuments || [],
                                    };
                                    
                                    // Add note to the appropriate notes array
                                    const existingNotes = payload[notesField] || [];
                                    payload[notesField] = [...existingNotes, newNote];

                                    await dispatch(adminupdateVerification({ id: localRequest.id, data: payload }));
                                    
                                    // Send notification to user
                                    try {
                                        const notificationMessage = customText 
                                            ? `Admin has requested additional ${verificationType.toLowerCase()} documents for your verification. Note: "${customText}". Please upload the required documents to complete your verification.`
                                            : `Admin has requested additional ${verificationType.toLowerCase()} documents for your verification. Please upload the required documents to complete your verification.`;
                                        
                                        await createUserNotificationAction({
                                            title: "Additional Documents Required",
                                            message: notificationMessage,
                                            status: { id: NotificationStatusEnum.Unread },
                                            priority: { id: NotificationPriorityEnum.High },
                                            type: { id: NotificationTypeEnum["In-App"] },
                                            user: { id: user.id },
                                            url: "/verify"
                                        });
                                    } catch (error) {
                                        console.error("Failed to send notification:", error);
                                    }
                                    
                                    // Clear the note input after adding
                                    setNote("");
                                    
                                    // Refresh the verification data
                                    if (onRefresh) {
                                        onRefresh();
                                    }
                                }}
                                disabled={isLoading}
                            >
                                <Mail className="h-4 w-4" />
                                Request Additional Info
                            </Button>
                            <Button
                                variant="outline"
                                className="flex items-center gap-2 text-green-600"
                                onClick={() => handleDecision(VerificationStatusEnum.accepted)}>
                                <CheckCircle className="h-4 w-4" />
                                Approve Request
                            </Button>
                            <Button
                                variant="outline"
                                className="flex items-center gap-2 text-red-600"
                                onClick={() => handleDecision(VerificationStatusEnum.rejected)}>
                                <X className="h-4 w-4" />
                                Reject Request
                            </Button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default VerificationDetailModal;